import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, PenLine, UserCheck, UserPlus } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import PersonSignOffDialog from '../../components/induction/PersonSignOffDialog';
import WitnessedAckDialog from '../../components/induction/WitnessedAckDialog';
import { InductionAccessDenied, InductionSkeleton, StatusBadge } from '../../components/induction/InductionGuards';
import { useInductionSeats } from '../../components/induction/useInductionSeats';
import { BASIS_LABELS, WORKER_TYPES, personLabel } from '../../components/induction/inductionLabels';
import inductionService from '../../services/induction';
import { errorMessage } from '../../utils/apiResponse';
import { formatDateInTz, formatDateTimeInTz, formatLocalDateInTz, useOrgTimezone } from '../../utils/timezone';
import type { PersonDetail } from '../../types/induction';

/** /inductions/people/:ref — one person's sign-offs, pack, acknowledgements and certifications. */
export default function InductionPerson() {
  const { ref = '' } = useParams<{ ref: string }>();
  const tz = useOrgTimezone();
  const { loading: seatsLoading, hasAccess, canAssess } = useInductionSeats();
  const [detail, setDetail] = useState<PersonDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [signing, setSigning] = useState(false);
  const [witnessing, setWitnessing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!ref) return;
    setLoading(true);
    setError(null);
    try {
      setDetail(await inductionService.person(ref));
    } catch (e) {
      setError(errorMessage(e, 'Could not load this person'));
    } finally {
      setLoading(false);
    }
  }, [ref]);
  useEffect(() => { if (hasAccess) load(); }, [hasAccess, load]);

  const records = useMemo(() => detail?.records ?? [], [detail]);
  const live = useMemo(() => records.filter(r => r.status === 'current' || r.status === 'expiring'), [records]);
  const held = useMemo(() => new Set(live.map(r => r.induction_id)), [live]);
  const duePolicies = useMemo(() => (detail?.pack ?? []).filter(p => p.status !== 'acknowledged'), [detail]);

  if (seatsLoading) return <InductionSkeleton />;
  if (!hasAccess) return <InductionAccessDenied />;

  const person = detail?.person;
  const personRef = person ? { ref: person.ref, name: person.name } : null;

  return (
    <>
      <PageHeader
        title={person ? personLabel(person.name) : 'Person'}
        subtitle={person
          ? [WORKER_TYPES[person.worker_type] ?? person.worker_type, person.picker_code, person.active ? null : 'inactive'].filter(Boolean).join(' · ')
          : undefined}
        icon={<UserCheck className="h-6 w-6 text-white" />}
        actionButtons={person ? (
          <div className="flex flex-wrap gap-2">
            {canAssess && (
              <Button size="sm" onClick={() => setSigning(true)}><UserPlus className="h-4 w-4" /> Sign off on…</Button>
            )}
            <Button size="sm" variant="secondary" onClick={() => setWitnessing(true)}>
              <PenLine className="h-4 w-4" /> Witnessed acknowledgement
            </Button>
          </div>
        ) : undefined}
      />
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">
        <Link to="/inductions" className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700">
          <ArrowLeft className="h-4 w-4" /> Register
        </Link>

        {loading && !detail && <p className="text-sm text-neutral-500">Loading…</p>}
        {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 break-words">{error}</div>}
        {notice && <div className="rounded-lg border border-success-200 bg-success-50 p-3 text-sm text-success-800 break-words">{notice}</div>}

        {detail && (
          <>
            <Card title="Equipment">
              {records.length === 0 ? (
                <p className="text-sm text-neutral-500">Not signed off on any equipment.</p>
              ) : (
                <ul className="divide-y divide-neutral-100">
                  {records.map(r => (
                    <li key={r.id} className={`py-3 text-sm ${r.status === 'revoked' ? 'text-neutral-500' : ''}`}>
                      <div className="flex flex-wrap items-center gap-2">
                        <Link to={`/inductions/equipment/${r.induction_id}`} className="min-w-0 font-medium text-primary-700 hover:underline break-words">
                          {r.induction_title}
                        </Link>
                        <StatusBadge status={r.status} />
                      </div>
                      <div className="mt-1 text-neutral-600 break-words">
                        {BASIS_LABELS[r.basis]} · {[r.assessed_by_name, formatDateInTz(r.assessed_at, tz)].filter(Boolean).join(', ')}
                        {r.expires_on && ` · expires ${formatLocalDateInTz(r.expires_on, tz)}`}
                      </div>
                      {r.status !== 'revoked' && r.content_version < r.current_version && (
                        <div className="mt-0.5 text-xs text-warning-800">The induction has changed since they were signed off.</div>
                      )}
                      {r.notes && <p className="mt-1 text-xs text-neutral-500 break-words">{r.notes}</p>}
                      {r.status === 'revoked' && (
                        <p className="mt-1 text-xs text-error-700 break-words">
                          Revoked{r.revoked_at ? ` ${formatDateInTz(r.revoked_at, tz)}` : ''}{r.revoked_by_name ? ` by ${r.revoked_by_name}` : ''}
                          {r.revoked_reason ? `: ${r.revoked_reason}` : ''}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card title="Induction pack">
              {detail.pack.length === 0 ? (
                <p className="text-sm text-neutral-500">No induction-pack policies apply to them.</p>
              ) : (
                <ul className="divide-y divide-neutral-100">
                  {detail.pack.map(p => (
                    <li key={p.id} className="flex flex-wrap items-start justify-between gap-2 py-2 text-sm">
                      <span className="min-w-0 flex-1 basis-40 break-words text-neutral-800">{p.title}</span>
                      <span className="min-w-0 max-w-full text-xs break-words">
                        {p.status === 'acknowledged' && p.acknowledged_at && (
                          <span className="text-success-700">
                            Acknowledged {formatDateTimeInTz(p.acknowledged_at, tz)}
                            {p.acknowledged_method === 'witnessed' && ' (witnessed)'}
                          </span>
                        )}
                        {p.status === 'updated' && <span className="text-warning-800">Updated — due again</span>}
                        {p.status === 'due' && <span className="text-error-700">Due</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card title="Acknowledgements">
              {detail.acknowledgements.length === 0 ? (
                <p className="text-sm text-neutral-500">Nothing acknowledged yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="text-left text-xs font-medium text-neutral-500">
                      <tr>
                        <th className="py-2 pr-3">What</th>
                        <th className="py-2 pr-3">Version</th>
                        <th className="py-2 pr-3">How</th>
                        <th className="py-2 pr-3">Recorded by</th>
                        <th className="py-2">When</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                      {detail.acknowledgements.map(a => (
                        <tr key={a.id} className="align-top">
                          <td className="py-2 pr-3 min-w-[10rem] break-words">{a.title}</td>
                          <td className="py-2 pr-3">{a.version}</td>
                          <td className="py-2 pr-3 whitespace-nowrap">
                            {a.method === 'witnessed' ? 'Witnessed' : 'Themselves'}
                            {a.signed_name && <span className="block text-xs text-neutral-500">“{a.signed_name}”</span>}
                          </td>
                          <td className="py-2 pr-3 whitespace-nowrap">{a.recorded_by_name ?? '—'}</td>
                          <td className="py-2 whitespace-nowrap">{formatDateTimeInTz(a.acknowledged_at, tz)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            <Card title="Certifications">
              {detail.certifications.length === 0 ? (
                <p className="text-sm text-neutral-500 break-words">
                  {person?.user_id ? 'None recorded.' : 'Certifications are recorded against people with a login.'}
                </p>
              ) : (
                <ul className="divide-y divide-neutral-100">
                  {detail.certifications.map(c => (
                    <li key={c.id} className="py-2 text-sm">
                      <span className="font-medium text-neutral-800 break-words">{c.type_name}</span>
                      <span className="block text-xs text-neutral-500 break-words">
                        {[c.credential_number, c.expiry_date ? `expires ${formatLocalDateInTz(c.expiry_date.slice(0, 10), tz)}` : 'no expiry'].filter(Boolean).join(' · ')}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <Link to="/certifications/admin" className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:underline">
                <ExternalLink className="h-4 w-4 shrink-0" /> Staff Certifications
              </Link>
            </Card>
          </>
        )}
      </div>

      {signing && personRef && (
        <PersonSignOffDialog
          person={personRef}
          held={held}
          onClose={() => setSigning(false)}
          onDone={n => { setSigning(false); setNotice(`Signed off on ${n} induction${n === 1 ? '' : 's'}.`); load(); }}
        />
      )}
      {witnessing && personRef && (
        <WitnessedAckDialog
          person={personRef}
          policies={duePolicies}
          records={live}
          onClose={() => setWitnessing(false)}
          onDone={title => { setWitnessing(false); setNotice(`Recorded their acknowledgement of ${title}.`); load(); }}
        />
      )}
    </>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="bg-white border border-neutral-200 rounded-xl p-4 sm:p-6">
      <h2 className="font-semibold text-neutral-900 mb-2">{title}</h2>
      {children}
    </section>
  );
}
